// i18nLocalesOverride: ['es-ar','pt-BR']
/* eslint-disable camelcase */
import { FunctionComponent, useCallback } from 'react';

import { ListItem } from '@andes/list';
import Edit16 from '@andes/icons/Edit24';

import { Button, ButtonText } from '@andes/button';
import { Card } from '@andes/card';

import { useI18n } from 'nordic/i18n';
import type { Step, StepInfo } from '../../../../../../services/types';
import type { HubWordings } from '../../../../../wordings';
import { handleExecuteNative, handleReloadNative } from '../../../../../utils/executeNative';

import StepMapper from '../mapper';
import { type PageContextType, usePage } from '../../../../../contexts/PageContext';
import { useStepsContext, type StepsContextProps } from '../StepsContext';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { useEditEmail } from '../../../../../hooks/useEditEmail';
import Thumbnail from '../thumbnail';
import { useHubCongrats } from '../../../../../hooks/useHubCongrats';
import { isMP } from '../../../../../utils';

type Props = {
  step: StepInfo
  wordings: HubWordings,
  stepsPendingWordings: HubWordings
  animatedCongratsStep: Step | undefined,
  feedbackStepUrl: string
}

const StepItem: FunctionComponent<Props> = (props: Props) => {
  const { step, wordings, stepsPendingWordings, animatedCongratsStep, feedbackStepUrl } = props;
  const { steps } = wordings;
  const { isWebview, platformId, isMobile } = usePage() as PageContextType;
  const { asset: Asset, congratsAsset: CongratsAsset, conflictAsset: ConflictAsset, description } = StepMapper[step.id];

  const animationCallback = () => {
    if (step.status === 'conflict' && step.id === animatedCongratsStep) {
      handleExecuteNative(isWebview, feedbackStepUrl, 'push', {});
    }
  };

  const {
    isLoadingCongratsStep,
    congratsStepClassName,
    isCongratsStep,
  } = useHubCongrats({ step, animatedCongratsStep, callback: animationCallback });

  const assetToRender = () => {
    if (!isLoadingCongratsStep || !isCongratsStep) {
      if (step.status === 'completed' || step.status === 'conflict') {
        return CongratsAsset;
      }
      return Asset;
    }
    return Asset;
  };

  const { i18n } = useI18n();
  const DEFAULT_ERROR_MESSAGE = i18n.gettext('Ocurrió un error, por favor vuelve a intentarlo.');

  const {
    urlToDeclaredEmail,
    isEditableStep,
    isRegulatedStep,
    editDeclaredEmailTrack,
  } = useStepsContext() as StepsContextProps;
  const { updateDeclaredEmail, isLoading } = useEditEmail();

  const { setShow, snackbar } = useSnackbar({ message: DEFAULT_ERROR_MESSAGE, type: 'error' });

  const handleClick = (uri: string, stepId: string) => {
    handleExecuteNative(
      isWebview,
      uri,
      'push',
      {},
      undefined,
      isRegulatedStep({
        stepId,
        uri,
        isMobile,
      }),
    );
  };

  const trackEditDeclaredEmail = useCallback(() => {
    const { melidata } = window;
    if (melidata && !isWebview) {
      melidata('send', 'event', editDeclaredEmailTrack);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDeclaredEmailTrack]);

  const getUrlToDeclaredEmail = async (stepId: string) => {
    try {
      const urlToEdit = await updateDeclaredEmail(urlToDeclaredEmail as string);
      trackEditDeclaredEmail();
      handleClick(urlToEdit, stepId);
      handleReloadNative(isWebview);
    } catch (e) {
      setShow(true);
    }
  };

  const getTertiaryButton = () => {
    const getSrLabel = () => steps[step.id]?.ariaLabel ?? steps[step.id]?.action;
    if (!isLoadingCongratsStep && step.uri) {
      return (
        <Button
          id="hub-item-button"
          size="medium"
          onClick={() => handleClick(step.uri!, step.id)}
          className="hub-item__button"
          srLabel={getSrLabel()}
        >
          <ButtonText>{steps[step.id]?.action}</ButtonText>
        </Button>
      );
    }

    if (isEditableStep!(step.id) && !isLoadingCongratsStep) {
      return (
        <Button
          id="hub-item-button-edit"
          loading={isLoading}
          hierarchy="transparent"
          size="small"
          onClick={() => getUrlToDeclaredEmail(step.id)}
          className="hub-item__button-edit"
          srLabel={getSrLabel()}
        >
          <Edit16 />
        </Button>
      );
    }

    return null;
  };

  const listItem = (
    <ListItem
      id={`step-item-${step.id}`}
      className="step-list__item"
      key={step.id}
      title={isCongratsStep && isLoadingCongratsStep
        ? stepsPendingWordings.steps[step.id]?.title
        : steps[step.id]?.title}
      description={isCongratsStep && isLoadingCongratsStep
        ? description(stepsPendingWordings.steps[step.id]?.description, step.data)
        : description(steps[step.id]?.description, step.data)}
      rightContent={getTertiaryButton()}
      leftContent={(
        <Thumbnail
          step={step}
          asset={assetToRender()}
          isLoadingCongrats={isLoadingCongratsStep}
          congratsClassName={isMP(platformId) ? congratsStepClassName : 'pending'}
          shouldShowCongratsAnimation={isCongratsStep}
        />
      )}
    />
  );


  return (
    <>
      {(step.uri && !isLoadingCongratsStep) || (isLoadingCongratsStep && step.id === animatedCongratsStep) ? (
        <Card
          id={`step-${step.id}`}
          className="step-list__card"
          shadow="elevated"
        >
          {listItem}
        </Card>
      ) : listItem}
      { snackbar }
    </>
  );
};

export default StepItem;
